import React from "react";

import {
  resolveOperatorMaxConcurrentCalls,
  resolveOperatorWsUrl,
  resolveSocketUrl,
} from "~/services/calling/config";
import { adaptOperatorListMessage } from "~/services/calling/adapters";
import type { CallState } from "~/context/call/callState";
import {
  agentsAreEqual,
  normalizeSocketIdentityValue,
  normalizeAgent,
} from "~/context/call/helpers";
import type { SipLifecycleIdentity } from "~/context/call/useSipLifecycle";

export type UseOperatorPresenceParams = {
  operatorPresenceSocketRef: React.MutableRefObject<WebSocket | null>;
  operatorPresenceKeepAliveRef: React.MutableRefObject<number | null>;
  operatorPresenceReconnectRef: React.MutableRefObject<number | null>;
  operatorPresenceShouldReconnectRef: React.MutableRefObject<boolean>;
  /** Stamped with `Date.now()` whenever a realtime agent roster arrives. */
  realtimeAgentsUpdatedAtRef: React.MutableRefObject<number>;
  sipIdentityRef: React.MutableRefObject<SipLifecycleIdentity>;
  updateState: (next: Partial<CallState>) => void;
  setState: React.Dispatch<React.SetStateAction<CallState>>;
  isAuthenticated: boolean;
  userExtensionId?: string | null;
  userSipExtension: string | null;
  userId?: string;
  userEmail?: string | null;
  userName?: string | null;
  userSurname?: string | null;
  scheduleReconnect: () => void;
};

export type UseOperatorPresenceResult = {
  initializeOperatorPresence: () => void;
  stopOperatorPresence: (shouldReconnect?: boolean) => void;
};

export function useOperatorPresence({
  operatorPresenceSocketRef,
  operatorPresenceKeepAliveRef,
  operatorPresenceReconnectRef,
  operatorPresenceShouldReconnectRef,
  realtimeAgentsUpdatedAtRef,
  sipIdentityRef,
  updateState,
  setState,
  isAuthenticated,
  userExtensionId,
  userSipExtension,
  userId,
  userEmail,
  userName,
  userSurname,
  scheduleReconnect,
}: UseOperatorPresenceParams): UseOperatorPresenceResult {
  const lastPresenceKeyRef = React.useRef<string | null>(null);

  const stopOperatorPresence = React.useCallback(
    (shouldReconnect = false) => {
      operatorPresenceShouldReconnectRef.current = shouldReconnect;
      if (operatorPresenceKeepAliveRef.current) {
        window.clearInterval(operatorPresenceKeepAliveRef.current);
        operatorPresenceKeepAliveRef.current = null;
      }
      if (operatorPresenceReconnectRef.current) {
        window.clearTimeout(operatorPresenceReconnectRef.current);
        operatorPresenceReconnectRef.current = null;
      }
      const socket = operatorPresenceSocketRef.current;
      if (!socket) return;
      socket.onopen = null;
      socket.onclose = null;
      socket.onerror = null;
      socket.onmessage = null;
      if (
        socket.readyState === WebSocket.OPEN ||
        socket.readyState === WebSocket.CONNECTING
      ) {
        socket.close();
      }
      operatorPresenceSocketRef.current = null;
      lastPresenceKeyRef.current = null;
    },
    [
      lastPresenceKeyRef,
      operatorPresenceKeepAliveRef,
      operatorPresenceReconnectRef,
      operatorPresenceShouldReconnectRef,
      operatorPresenceSocketRef,
    ]
  );

  const initializeOperatorPresence = React.useCallback(() => {
    const socketUrl = resolveSocketUrl();
    const operatorWsUrl = resolveOperatorWsUrl(socketUrl);
    if (!operatorWsUrl) return;

    const extensionFromUser =
      normalizeSocketIdentityValue(userSipExtension) ??
      normalizeSocketIdentityValue(userExtensionId);
    const resolvedExtension =
      extensionFromUser ?? sipIdentityRef.current.extension ?? undefined;
    const resolvedSipUsername =
      sipIdentityRef.current.sipUsername ?? resolvedExtension ?? undefined;
    const routingExtension = resolvedExtension ?? resolvedSipUsername;

    if (!routingExtension) return;

    const presenceKey = `${operatorWsUrl}|${routingExtension}`;
    const existing = operatorPresenceSocketRef.current;
    if (
      existing &&
      lastPresenceKeyRef.current === presenceKey &&
      (existing.readyState === WebSocket.OPEN ||
        existing.readyState === WebSocket.CONNECTING)
    ) return;

    stopOperatorPresence(true);

    const displayName =
      [userName, userSurname].filter(Boolean).join(" ").trim() ||
      userEmail ||
      "Operator";
    const maxConcurrentCalls = resolveOperatorMaxConcurrentCalls(2);

    let ws: WebSocket;
    try {
      ws = new WebSocket(operatorWsUrl);
    } catch {
      return;
    }

    operatorPresenceSocketRef.current = ws;
    lastPresenceKeyRef.current = presenceKey;
    operatorPresenceShouldReconnectRef.current = true;

    const sendRegister = () => {
      if (ws.readyState !== WebSocket.OPEN) return;
      const payload = {
        type: "register",
        extension: routingExtension,
        userId: userId ?? "",
        name: displayName,
        maxConcurrentCalls,
      };
      try {
        ws.send(JSON.stringify(payload));
      } catch {
        // Ignore send failures; reconnect will retry.
      }
    };

    const sendHeartbeat = () => {
      if (ws.readyState !== WebSocket.OPEN) return;
      try {
        ws.send(JSON.stringify({ type: "heartbeat" }));
      } catch {
        // Ignore heartbeat send failures.
      }
    };

    ws.onopen = () => {
      sendRegister();
      sendHeartbeat();
      if (operatorPresenceKeepAliveRef.current) {
        window.clearInterval(operatorPresenceKeepAliveRef.current);
      }
      let tick = 0;
      operatorPresenceKeepAliveRef.current = window.setInterval(() => {
        tick += 1;
        if (tick % 2 === 0) sendRegister();
        sendHeartbeat();
      }, 30000);
    };

    ws.onmessage = (event) => {
      let payload: unknown = event.data;
      if (typeof event.data === "string") {
        try {
          payload = JSON.parse(event.data);
        } catch {
          return;
        }
      }
      const agents = adaptOperatorListMessage(payload);
      if (agents) {
        realtimeAgentsUpdatedAtRef.current = Date.now();
        const nextAgents = agents.map(normalizeAgent);
        setState((prev) =>
          agentsAreEqual(prev.agents, nextAgents)
            ? prev
            : { ...prev, agents: nextAgents }
        );
      }
    };

    ws.onerror = () => {};

    ws.onclose = () => {
      if (operatorPresenceKeepAliveRef.current) {
        window.clearInterval(operatorPresenceKeepAliveRef.current);
        operatorPresenceKeepAliveRef.current = null;
      }
      operatorPresenceSocketRef.current = null;
      lastPresenceKeyRef.current = null;

      if (!operatorPresenceShouldReconnectRef.current || !isAuthenticated) {
        return;
      }

      if (operatorPresenceReconnectRef.current) {
        window.clearTimeout(operatorPresenceReconnectRef.current);
      }
      operatorPresenceReconnectRef.current = window.setTimeout(() => {
        operatorPresenceReconnectRef.current = null;
        scheduleReconnect();
      }, 1500);
    };
  }, [
    realtimeAgentsUpdatedAtRef,
    lastPresenceKeyRef,
    operatorPresenceKeepAliveRef,
    operatorPresenceReconnectRef,
    operatorPresenceShouldReconnectRef,
    operatorPresenceSocketRef,
    sipIdentityRef,
    stopOperatorPresence,
    scheduleReconnect,
    setState,
    updateState,
    isAuthenticated,
    userEmail,
    userExtensionId,
    userId,
    userName,
    userSipExtension,
    userSurname,
  ]);

  return { initializeOperatorPresence, stopOperatorPresence };
}
